import {
	Box,
	Button,
	Card,
	Divider,
	Grid,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import {
	useDeleteMomentMutation,
	useGetMomentDetailQuery,
} from '@/api/app-setting/momentApiSlice';
import MomentInfoForm from './components/MomentInfoForm';
import MomentDetailListSummaryPage from './MomentDetailListSummaryPage';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function MomentDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteMoment] = useDeleteMomentMutation();
	const { momentId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');

	const { data: details } = useGetMomentDetailQuery(momentId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('moment.delete')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('moment.isDelete')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteMoment(momentId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/app-setting/moment-list', { replace: true });
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.momentDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('moment.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('moment.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('moment.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>{t('moment.text')}</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('moment.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('moment.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('moment.title')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>{t('moment.text')}</Typography>
										</Box>
										<MomentInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
				<Card sx={{ padding: '24px', marginTop: 3 }}>
					<MomentDetailListSummaryPage />
				</Card>
			</Box>
		</>
	);
}
